import {
  ChangeDetectionStrategy,
  Component,
  ViewChild,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ClientAccessStatusResponse } from '@interfaces/partners/responses/client-access.response';
import { ClientAccessHttpService } from '@services/http/partners/client-access-http.service';
import { VaultDateTimePipe } from '@src/app/shared/pipes/vault-date-time.pipe';
import { SharedModule } from '@src/app/shared/shared.module';
import { catchError, filter, of, switchMap, tap } from 'rxjs';
import { describeClientError } from '@src/app/shared/utils/client-error-message';
import { ModalClientAccessComponent } from '../modal-client-access/modal-client-access.component';
import { clientAccessBadge } from './client-access-badge';

/**
 * Card "Acesso do cliente" do deal (card 3461; protótipo `v2/core.jsx`, ConviteClienteCard).
 *
 * O master do parceiro ou o time interno criam o usuário da empresa (nome + e-mail) e o sistema manda o
 * e-mail de criar senha: o cliente assina o NDA + carta SCR pela área dele (Comercial › Minha
 * operação), sem esperar o termo chegar por outro caminho.
 *
 * Fica na aba **KYC** para o time interno (ou na **Visão geral**, quando a KYC ainda não aparece) e na
 * **Visão geral** para o master do parceiro. Quem decide se o card
 * aparece é o pai, pela permissão `COMMERCIAL_INVITE_CLIENT`: aqui ele só carrega e desenha.
 */
@Component({
  selector: 'app-client-access-card',
  standalone: true,
  imports: [SharedModule, VaultDateTimePipe, ModalClientAccessComponent],
  templateUrl: './client-access-card.component.html',
  styleUrl: './client-access-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientAccessCardComponent {
  readonly dealId = input.required<string>();

  /** Razão social da empresa do deal, para o título da modal. */
  readonly companyName = input<string | null>(null);

  /** Deal recusado: o backend recusa o convite, então o card troca o botão pelo aviso. */
  readonly rejected = input(false);

  /** Avisa o pai que houve convite, para ele recarregar a linha do tempo do deal. */
  readonly invited = output<void>();

  @ViewChild('inviteModal') inviteModal?: ModalClientAccessComponent;

  readonly loading = signal(true);
  readonly errorMessage = signal<string | null>(null);
  readonly status = signal<ClientAccessStatusResponse | null>(null);

  readonly badge = computed(() => clientAccessBadge(this.status()));
  readonly users = computed(() => this.status()?.users ?? []);

  /** Incrementado pelo "Tentar de novo": recarrega sem trocar de deal. */
  private readonly reloadToken = signal(0);
  private readonly loadRequest = computed(() => ({
    dealId: this.dealId(),
    token: this.reloadToken(),
  }));

  constructor(private readonly clientAccessService: ClientAccessHttpService) {
    toObservable(this.loadRequest)
      .pipe(
        filter(request => !!request.dealId),
        tap(() => {
          this.loading.set(true);
          this.errorMessage.set(null);
        }),
        switchMap(request =>
          this.clientAccessService.getStatus(request.dealId).pipe(
            catchError(error => {
              this.errorMessage.set(
                describeClientError(
                  error,
                  'Não conseguimos ver o acesso do cliente agora. Confira a sua conexão e clique em Tentar de novo.'
                )
              );
              return of(null);
            })
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe(status => {
        this.status.set(status);
        this.loading.set(false);
      });
  }

  reload(): void {
    this.reloadToken.update(value => value + 1);
  }

  openInvite(): void {
    this.inviteModal?.open(this.dealId(), this.companyName());
  }

  /** A modal devolve a situação atualizada; sem ela (resposta vazia), recarrega. */
  onInvited(status: ClientAccessStatusResponse | null): void {
    if (status) {
      this.status.set(status);
      this.errorMessage.set(null);
    } else {
      this.reload();
    }
    this.invited.emit();
  }
}
